import React from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip, Stack, Box, Typography } from '@mui/material'
import { CardBody, CardBlockInfo } from 'packages/ui/CardPreview'
import { WireLine } from '../../utils/types'
import { useUser } from 'store'
import { 
  getTechnologyLabel, 
  getTechnologyDescription, 
  getFunctionLabel, 
  getFunctionDescription,
  formatBandwidth,
  getDurationParts
} from '../../utils/helpers'

interface BodyProps {
  wireLine: WireLine;
}

const StatCard = ({ value, label, tooltip }: { value: string | number | React.ReactNode; label: string; tooltip?: string }) => (
  <Box
    sx={{
      flex: 1,
      border: '1px solid #EFEFEF',
      borderRadius: '12px',
      p: 1.5,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '70px'
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, fontSize: '16px', fontWeight: 500 }}>
      {value}
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
      <Typography sx={{ fontSize: 9, color: '#656565', textAlign: 'center' }}>
        {label}
      </Typography>
      {tooltip && (
        <Tooltip title={tooltip} placement="bottom" arrow>
          <i className="fa-regular fa-circle-info" style={{ fontSize: '9px', color: '#7A8096' }} />
        </Tooltip>
      )}
    </Box>
  </Box>
)

const Body: React.FC<BodyProps> = ({ wireLine }) => {
  const { t } = useTranslation()
  const [user] = useUser()
  const technologyLabel = getTechnologyLabel(wireLine.wire_type)
  const technologyDescription = getTechnologyDescription(wireLine.wire_type)
  const functionLabel = getFunctionLabel(
    wireLine.use_case,
    wireLine.owner_is_wifirst,
    wireLine.wan_access_through_ipsec
  )
  const functionDescription = getFunctionDescription(
    wireLine.use_case,
    wireLine.owner_is_wifirst,
    wireLine.wan_access_through_ipsec
  )

  const showSaturationStats = wireLine.use_case === 'wan_access' && (user?.admin || user?.roles?.wan_expert)

  const duration = getDurationParts(wireLine.total_loss_down);

  return (
    <>
      {showSaturationStats && (
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" gap={1.5} sx={{ width: '100%' }}>
            <StatCard 
              value={duration ? (
                <>
                  <Box component="span" sx={{ fontSize: '18px' }}>{duration.hours}</Box>
                  <Box component="span" sx={{ color: '#999', fontSize: '14px' }}>h</Box>{' '}
                  <Box component="span" sx={{ fontSize: '18px' }}>{duration.minutes}</Box>
                  <Box component="span" sx={{ color: '#999', fontSize: '14px' }}>min</Box>
                </>
              ) : '-'}
              label={t('wan.card.total_saturation_time_short')}
              tooltip={t('wan.card.total_saturation_time_tooltip')}
            />
            <StatCard 
              value={wireLine.nb_days_loss ?? 0} 
              label={t('wan.card.days_with_saturation_short')}
              tooltip={t('wan.card.days_with_saturation_tooltip')}
            />
          </Stack>
        </Box>
      )}
      
      <CardBody grid>
        <CardBlockInfo
          title={t('wan.card.provider')}
          subtitle={wireLine.owner_is_wifirst ? 'Wifirst' : 'Client'}
        />
        {
         // eslint-disable-next-line no-extra-boolean-cast
         Boolean(technologyDescription) ? (
            <CardBlockInfo
              title={t('wan.card.technology')}
              subtitle={<Tooltip title={technologyDescription} placement="bottom" arrow>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {technologyLabel}
                    <i className="fa-regular fa-circle-info" style={{ marginLeft: '5px', fontSize: '12px', color: '#7A8096' }} />
                  </div>
                </Tooltip>}
            />
          ) : (
            <CardBlockInfo
              title={t('wan.card.technology')}
              subtitle={technologyLabel}
            />
          )
        }
        <CardBlockInfo
          title={t('wan.card.function')}
          subtitle={functionDescription ? (
            <Tooltip title={functionDescription} placement="bottom" arrow>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {functionLabel}
                <i className="fa-regular fa-circle-info" style={{ marginLeft: '5px', fontSize: '12px', color: '#7A8096' }} />
              </div>
            </Tooltip>
          ) : functionLabel}
        />
        <CardBlockInfo
          title={t('wan.card.ip_address')}
          subtitle={wireLine.effective_ip_address || '-'}
        />
        <CardBlockInfo
          title={t('wan.card.download')}
          subtitle={formatBandwidth(wireLine.download, false, true, wireLine.wire_type)}
        />
        <CardBlockInfo
          title={t('wan.card.upload')}
          subtitle={formatBandwidth(wireLine.upload, false, true, wireLine.wire_type)}
        />
      </CardBody>
    </>
  )
}

export default Body
